import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { topicoComandosTela, topicoStatusTela } from '@/shared/lib/canaisTela';
import type { MusicaAtualBroadcast, PedidoIniciadoBroadcast, JukeboxBroadcastState } from '@/shared/models/jukebox.model';
import type { UpdateSettingsBroadcast } from '@/shared/models/configuracoes.model';
import type { PdvEngajamentoBroadcast } from '@/shared/models/pdv.model';
import { montarTextoDestaque } from '@/features/pdv-engajamento';
import { telaDeDbChange, type TelaPlaylistPatch } from '../../domain/usecases/trocaPlaylistTela';

export type { PedidoIniciadoBroadcast, JukeboxBroadcastState } from '@/shared/models/jukebox.model';

export interface JukeboxBroadcastControls {
  criarCanal(): void;
  destruirCanal(): void;
}

export function useJukeboxBroadcast(
  telaId: string | undefined,
  initialMusicaAtual: MusicaAtualBroadcast | null = null,
  onReloadPlaylist?: () => void,
  meuSessionId?: string,
  onTelaAtualizada?: (tela: TelaPlaylistPatch) => void,
): JukeboxBroadcastState & JukeboxBroadcastControls {
  const [musicaAtual,           setMusicaAtual]           = useState<MusicaAtualBroadcast | null>(initialMusicaAtual);
  const [pedidoIniciado,        setPedidoIniciado]        = useState<PedidoIniciadoBroadcast | null>(null);
  const [sugestaoAprovada,      setSugestaoAprovada]      = useState<{ titulo: string; thumbnail: string } | null>(null);
  const [sugestaoRecusada,      setSugestaoRecusada]      = useState<{ titulo: string; thumbnail: string } | null>(null);
  const [ultimaAtividadePlayer, setUltimaAtividadePlayer] = useState<number | null>(null);
  const [batalhaVibesAtiva,     setBatalhaVibesAtiva]     = useState<boolean | null>(null);
  const [temaDiaNamorados,      setTemaDiaNamorados]      = useState<boolean | null>(null);
  const [temaFestaJunina,       setTemaFestaJunina]       = useState<boolean | null>(null);
  const [temaCopa,              setTemaCopa]              = useState<boolean | null>(null);
  const [pdvToast,              setPdvToast]              = useState<string | null>(null);

  const channelRef              = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const canalCmdRef             = useRef<ReturnType<typeof supabase.channel> | null>(null);
  // removeChannel só tira o tópico da lista no close. channel() antes disso
  // devolve a instância em `leaving`, e subscribe() não entra se !isClosed().
  const remocaoRef              = useRef<Promise<unknown>>(Promise.resolve());
  const remocaoProntaRef        = useRef(true);
  const geracaoRef              = useRef(0);
  const onReloadPlaylistRef     = useRef(onReloadPlaylist);
  const meuSessionIdRef         = useRef(meuSessionId);
  const onTelaAtualizadaRef     = useRef(onTelaAtualizada);
  const pdvToastTimerRef          = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pedidoIniciadoTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sugestaoAprovadaTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sugestaoRecusadaTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { onReloadPlaylistRef.current = onReloadPlaylist; });
  useEffect(() => { meuSessionIdRef.current     = meuSessionId;    });
  useEffect(() => { onTelaAtualizadaRef.current = onTelaAtualizada; });

  useEffect(() => {
    if (initialMusicaAtual === null) return;
    setMusicaAtual(prev => prev ?? initialMusicaAtual);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMusicaAtual?.youtube_video_id]);

  const destruirCanal = useCallback(() => {
    geracaoRef.current++;
    const canaisAtivos = [channelRef.current, canalCmdRef.current].filter(
      (c): c is NonNullable<typeof c> => c != null,
    );
    if (canaisAtivos.length > 0) {
      remocaoProntaRef.current = false;
      const pendente = Promise.all(
        canaisAtivos.map((c) => Promise.resolve(supabase.removeChannel(c)).catch(() => {})),
      );
      remocaoRef.current = pendente;
      void pendente.then(() => {
        if (remocaoRef.current === pendente) remocaoProntaRef.current = true;
      });
      channelRef.current = null;
      canalCmdRef.current = null;
    }
    if (pdvToastTimerRef.current) clearTimeout(pdvToastTimerRef.current);
    if (pedidoIniciadoTimerRef.current) clearTimeout(pedidoIniciadoTimerRef.current);
    if (sugestaoAprovadaTimerRef.current) clearTimeout(sugestaoAprovadaTimerRef.current);
    if (sugestaoRecusadaTimerRef.current) clearTimeout(sugestaoRecusadaTimerRef.current);
    setPdvToast(null);
    // Flags de broadcast vencem o banco (resolverComPrioridade): sem zerar
    // aqui, um update_settings visto antes do background vencia o valor que o
    // init relê na volta, e um update perdido no background nunca corrigia.
    setBatalhaVibesAtiva(null);
    setTemaDiaNamorados(null);
    setTemaFestaJunina(null);
    setTemaCopa(null);
  }, []);

  const criarCanal = useCallback(async () => {
    if (!telaId) return;
    destruirCanal();
    const geracao = geracaoRef.current;
    if (!remocaoProntaRef.current) await remocaoRef.current;
    if (geracao !== geracaoRef.current) return;

    const canalCmd = supabase
      .channel(topicoComandosTela(telaId), { config: { private: true } })
      .on('broadcast', { event: 'remote_cmd' }, ({ payload }) => {
        const cmd = (payload as { cmd?: string })?.cmd;
        if (cmd === 'sugestao_aprovada') {
          const data = payload as { sugestao: { titulo: string; thumbnail: string; session_id?: string } };
          const temSessionId = typeof data.sugestao.session_id === 'string' && data.sugestao.session_id.length > 0;
          const isMinhaSugestao = !temSessionId || data.sugestao.session_id === meuSessionIdRef.current;
          if (isMinhaSugestao) {
            setSugestaoAprovada(data.sugestao);
            if (sugestaoAprovadaTimerRef.current) clearTimeout(sugestaoAprovadaTimerRef.current);
            sugestaoAprovadaTimerRef.current = setTimeout(() => setSugestaoAprovada(null), 8_000);
          }
        } else if (cmd === 'sugestao_recusada') {
          const data = payload as { sugestao: { titulo: string; thumbnail: string; session_id?: string } };
          const temSessionId = typeof data.sugestao.session_id === 'string' && data.sugestao.session_id.length > 0;
          const isMinhaSugestao = !temSessionId || data.sugestao.session_id === meuSessionIdRef.current;
          if (isMinhaSugestao) {
            setSugestaoRecusada(data.sugestao);
            if (sugestaoRecusadaTimerRef.current) clearTimeout(sugestaoRecusadaTimerRef.current);
            sugestaoRecusadaTimerRef.current = setTimeout(() => setSugestaoRecusada(null), 8_000);
          }
        } else if (cmd === 'reload_playlist') {
          onReloadPlaylistRef.current?.();
        }
        // remote_cmd vem do Controle Remoto / Moderação / dashboard, nunca do
        // Player — não prova presença da TV (mesma regra do useComandoChannel).
      })
      .on('broadcast', { event: 'cmd' }, ({ payload }) => {
        const p = payload as UpdateSettingsBroadcast;
        if (p?.cmd === 'update_settings') {
          const cfg = p.configuracoes;
          if (typeof cfg?.config_batalha_vibes === 'boolean')       setBatalhaVibesAtiva(cfg.config_batalha_vibes);
          if (typeof cfg?.config_tema_dia_namorados === 'boolean')  setTemaDiaNamorados(cfg.config_tema_dia_namorados);
          if (typeof cfg?.config_tema_festa_junina === 'boolean')   setTemaFestaJunina(cfg.config_tema_festa_junina);
          if (typeof cfg?.config_tema_copa === 'boolean')           setTemaCopa(cfg.config_tema_copa);
        }
      })
      .on('broadcast', { event: 'pdv_engajamento' }, ({ payload }) => {
        const p = payload as PdvEngajamentoBroadcast;
        const texto = p?.tipo === 'destaque'
          ? montarTextoDestaque(p.itens).titulo
          : p?.tipo === 'hype_marco'
            ? `${p.marco} ${p.hype_rotulo} hoje! 🍻`
            : null;
        if (!texto) return;
        setPdvToast(texto);
        if (pdvToastTimerRef.current) clearTimeout(pdvToastTimerRef.current);
        pdvToastTimerRef.current = setTimeout(() => setPdvToast(null), 4_000);
      })
      .on('broadcast', { event: 'db_change' }, ({ payload }) => {
        const tela = telaDeDbChange(payload);
        if (!tela) return;
        onTelaAtualizadaRef.current?.(tela);
      })
      .subscribe();
    canalCmdRef.current = canalCmd;

    const channel = supabase
      .channel(topicoStatusTela(telaId))
      .on('broadcast', { event: 'musica_atual' }, ({ payload }) => {
        setMusicaAtual(payload as MusicaAtualBroadcast);
        setUltimaAtividadePlayer(Date.now());
      })
      .on('broadcast', { event: 'pedido_iniciado' }, ({ payload }) => {
        setPedidoIniciado(payload as PedidoIniciadoBroadcast);
        if (pedidoIniciadoTimerRef.current) clearTimeout(pedidoIniciadoTimerRef.current);
        pedidoIniciadoTimerRef.current = setTimeout(() => setPedidoIniciado(null), 8_000);
        setUltimaAtividadePlayer(Date.now());
      })
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        // Canal pronto: solicita o estado atual da TV imediatamente.
        // Garante que a resposta do Player chega no canal já subscrito,
        // eliminando a corrida com o init() que enviava antes do canal estar pronto.
        channel.send({
          type:    'broadcast',
          event:   'jukebox_cmd',
          payload: { cmd: 'solicitar_musica_atual' },
        }).catch(() => {});
      });
    channelRef.current = channel;
  }, [telaId, destruirCanal]);

  useEffect(() => {
    void criarCanal();
    return destruirCanal;
  }, [criarCanal, destruirCanal]);

  return {
    musicaAtual, pedidoIniciado, sugestaoAprovada, sugestaoRecusada,
    ultimaAtividadePlayer, batalhaVibesAtiva, temaDiaNamorados, temaFestaJunina, temaCopa, pdvToast,
    criarCanal, destruirCanal,
  };
}
